import { Injectable } from '@angular/core';
import { LottoStore } from './lotto.store';
import { LottoItem } from './models/lotto-item.model';
import { tap, switchMap, mapTo, catchError } from 'rxjs/operators';
import { ApiService } from '../../shared/services/core/api.service';
import { of, Observable, firstValueFrom, throwError } from 'rxjs';

import { LottoQuery } from './lotto.query';
import { LoggingService } from '../../shared/services/core/logging.service';
import { HttpErrorResponse } from '@angular/common/http';
import { FgbError } from '../../shared/services/core/fgb-error';
import { LottoDetails } from './lotto-v2/models/lotto.model';

@Injectable({ providedIn: 'root' })
export class LottoService {
  constructor(
    private lottoStore: LottoStore,
    private lottoQuery: LottoQuery,
    private api: ApiService,
    private logService: LoggingService
  ) {}

  /** Fetches the lottos and puts them in the store. */
  fetchLottos(): Observable<null> {
    this.lottoStore.setLoading(true);

    const request$: Observable<null> = this.api.get<LottoItem>('lottos').pipe(
      tap((lottos) => this.lottoStore.set(lottos)),
      mapTo(null)
    );

    return this.lottoQuery.getHasCache() ? of(null) : request$;
  }

  /** Fetches a specific lotto (ignoring cache) and puts it in the store. */
  fetchLottoById(id: number): Observable<null> {
    this.lottoStore.setLoading(true);

    const request$: Observable<null> = this.api.getById<LottoItem>('lottos', id.toString()).pipe(
      tap((lotto) => this.lottoStore.upsert(id, lotto)),
      mapTo(null)
    );

    return request$;
  }

  /** Enter a lotto. */
  async enterLotto(id: number, entries: number, memberId: string = ''): Promise<null> {
    this.logService.debug(`Entering lotto ${entries} time(s)`, id);

    const request$ = this.api.postForParent('lottos', id, 'enter?memberId=' + memberId, entries).pipe(
      tap({
        error: (error) => {
          if (error instanceof HttpErrorResponse) {
            if (error.status === 400) {
              // If the error is status 400, we need to fetch the lotto again to get the updated data.
              // but if we know the exact error code we can just update the store so we dont need to call another api call,
              // instead we will just fetch the lotto again just to be safe.
              firstValueFrom(this.fetchLottoById(id));
            }
          }
        },
      }),
      switchMap(() => this.fetchLottoById(id)),
      catchError((err: any) => throwError(() => new FgbError(err)))
    );

    return await firstValueFrom(request$);
  }

  /** Update the locked property on all lottos to true */
  lockAllItems() {
    this.lottoStore.update(null, { Locked: true });
  }

  /** Version 2
   *
   * Unlike V1, these methods do not write to the store.
   * Data is returned as plain Observables, keeping state management
   * out of the service for better scalability and flexibility.
   */
  fetchLottosV2(): Observable<LottoDetails[]> {
    return this.api.get<LottoDetails>('lottos/v2');
  }

  fetchLottoV2ById(id: number): Observable<LottoDetails> {
    return this.api.getById<LottoDetails>('lottos/v2', id.toString());
  }

  enterLottoV2(id: number, entries: number, CustomFields: any[] = []): Observable<any> {
    return this.api.post(`lottos/v2/${id}/enter`, { LottoId: id, NumberOfEntries: entries, CustomFields });
  }
}
